import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_ALL_DEPARTMENT_CATEGORY_LIST_REQUEST,
  GET_ALL_DEPARTMENT_INVENTORY_LIST_REQUEST,
  GET_ALL_DEPARTMENT_LIST_REQUEST,
} from "../../../../../../Utils/Constant";
import FormSelect from "../../../../../../Lib/FormSelect";

const ProcurementItems = ({ orderItems, setProcurementItems }) => {
  const dispatch = useDispatch();
  const {
    departmentList = [],
    departmentCategoryList = [],
    departmentInventoryList = [],
  } = useSelector((state) => state.department);

  const [errors, setErrors] = useState({});

  // Load all necessary data
  useEffect(() => {
    dispatch({ type: GET_ALL_DEPARTMENT_LIST_REQUEST });
    dispatch({
      type: GET_ALL_DEPARTMENT_CATEGORY_LIST_REQUEST,
      payload: { body: { isAll: true } },
    });
    dispatch({
      type: GET_ALL_DEPARTMENT_INVENTORY_LIST_REQUEST,
      payload: { body: { isAll: true } },
    });
  }, [dispatch]);

  // Memoize category options
  const departmentOprions = useMemo(() => {
    return (
      departmentList?.data?.departments?.map((category) => ({
        label: category.name,
        value: category.id,
      })) || []
    );
  }, [departmentList]);

  // Function to get subcategories for a specific category
  const getDepartmentCategoryOptions = useMemo(() => {
    const allSubs = departmentCategoryList?.data || [];
    return (departmentId) => {
      if (!departmentId) return [];
      return allSubs
        ?.filter((sub) => String(sub.department_id) === String(departmentId))
        ?.map((sub) => ({
          label: sub.name,
          value: sub.id,
        }));
    };
  }, [departmentCategoryList]);

  // Function to get variants for a specific subcategory
  const getItemOptions = useMemo(() => {
    const allVariants = departmentInventoryList?.data || [];
    return (categoryId) => {
      if (!categoryId) return [];
      return allVariants
        .filter((variant) => String(variant.category_id) === String(categoryId))
        .map((variant) => ({
          label: variant.name,
          value: variant.id,
          price: variant.cost_per_unit,
          unit: variant.unit,
          available_quantity: variant.quantity,
        }));
    };
  }, [departmentInventoryList]);

  const validateQuantities = (items, requestType) => {
    const newErrors = { ...errors };
    items.forEach((item, index) => {
      const maxQty = item.available_quantity || 0;
      const newQty = parseInt(item.quantity, 10) || 0;

      if (requestType === "internal") {
        if (newQty > maxQty) {
          newErrors[`quantity_${index}`] = `Cannot request more than ${maxQty}`;
        } else {
          delete newErrors[`quantity_${index}`];
        }
      } else {
        // Remove existing errors for external type
        if (newErrors[`quantity_${index}`]) {
          delete newErrors[`quantity_${index}`];
        }
      }
    });
    setErrors(newErrors);
  };

  // Handle quantity changes
  const changeQuantity = (index, value) => {
    const updated = [...orderItems.items];
    const newQty = parseInt(value, 10) || 0;
    updated[index].quantity = newQty;
    updated[index].total = (updated[index].unit_price || 0) * newQty;

    validateQuantities(updated, orderItems.request_type);

    setProcurementItems({ ...orderItems, items: updated });
  };

  // Handle price changes
  const handlePriceChange = (index, value) => {
    const updated = [...orderItems.items];
    const newPrice = parseFloat(value) || 0;
    updated[index].item_total_price = newPrice;

    setProcurementItems({ ...orderItems, items: updated });
  };

  // Handle field changes
  const handleChange = (index, field, value) => {
    const updated = [...orderItems.items];
    updated[index][field] = value;

    // Set price when variant is selected
    if (field === "item_id") {
      const selectedItem = getItemOptions(orderItems.category_id).find(
        (v) => v.value === value
      );
      if (selectedItem) {
        updated[index].unit_price = selectedItem.price || 0;
        updated[index].total =
          (selectedItem.price || 0) * (updated[index].quantity || 1);
        updated[index].item_total_price = 0;
        updated[index].unit = selectedItem.unit;
        updated[index].available_quantity =
          selectedItem.available_quantity || 0;
      }
    }

    // Recalculate total when quantity changes
    if (field === "quantity") {
      const unitPrice = updated[index].unit_price || 0;
      updated[index].total = unitPrice * value;
    }

    setProcurementItems({ ...orderItems, items: updated });
  };

  // Handle field category/department
  const handleChange1 = (field, value) => {
    const updated = { ...orderItems };

    // Reset dependent fields when category changes
    if (field === "department_id") {
      updated.department_id = value;
      updated.category_id = "";
      updated.items = [];
    }

    // Reset dependent fields when subcategory changes
    if (field === "category_id") {
      updated.category_id = value;
      updated.items = [];
    }
    setProcurementItems(updated);
  };

  // Add a new order item
  const addOrderItem = () => {
    setProcurementItems({
      ...orderItems,
      items: [
        ...orderItems.items,
        {
          item_id: "",
          quantity: 0,
          available_quantity: 0,
          unit: "",
          unit_price: 0,
          total: 0,
          reason: "",
        },
      ],
    });
  };

  // Remove an order item
  const removeOrderItem = (index) => {
    const updatedItems = orderItems?.items?.filter((_, i) => i !== index);
    setProcurementItems({ ...orderItems, items: updatedItems });
  };

  // Handle request type toggle
  const handleRequestTypeToggle = () => {
    const newRequestType =
      orderItems.request_type === "internal" ? "external" : "internal";
    const updatedOrder = {
      ...orderItems,
      request_type: newRequestType,
    };
    // Revalidate all items
    validateQuantities(updatedOrder.items, newRequestType);
    setProcurementItems(updatedOrder);
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="row">
          <div className="col-md-3 mb-2">
            <FormSelect
              label="Department"
              value={orderItems.department_id || ""}
              onChange={(e) => handleChange1("department_id", e.target.value)}
              options={departmentOprions}
              required
              disabled={orderItems?.id ? true : false}
            />
          </div>

          <div className="col-md-3 mb-2">
            <FormSelect
              label="Category"
              value={orderItems.category_id || ""}
              onChange={(e) => handleChange1("category_id", e.target.value)}
              options={getDepartmentCategoryOptions(orderItems.department_id)}
              required
              disabled={
                !orderItems.department_id || (orderItems.id ? true : false)
              }
            />
          </div>

          {/* Request Type Toggle Section */}
          <div className="col-md-6 mb-2">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <div>
                  <h6 className="mb-1">Request Type</h6>
                  <small className="text-muted">
                    {orderItems.request_type === "internal"
                      ? "Internal procurement request"
                      : "External vendor request"}
                  </small>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span
                    className={`fw-bold ${
                      orderItems.request_type === "internal"
                        ? "text-primary"
                        : "text-muted"
                    }`}
                  >
                    Internal
                  </span>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="requestTypeSwitch"
                      checked={orderItems.request_type === "external"}
                      onChange={handleRequestTypeToggle}
                      disabled={orderItems?.id ? true : false}
                      style={{ transform: "scale(1.2)" }}
                    />
                  </div>
                  <span
                    className={`fw-bold ${
                      orderItems.request_type === "external"
                        ? "text-success"
                        : "text-muted"
                    }`}
                  >
                    External
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Items</h5>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={addOrderItem}
          >
            <i className="bi bi-plus me-1"></i>Add Item
          </button>
        </div>

        {orderItems?.items?.map((item, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <div className="row mt-2">
              <div className="col-md-3 mb-2">
                <FormSelect
                  label="Items"
                  value={item.item_id || ""}
                  onChange={(e) =>
                    handleChange(index, "item_id", e.target.value)
                  }
                  options={getItemOptions(orderItems.category_id)}
                  required
                  disabled={!orderItems.category_id}
                />
              </div>

              <div className="col-md-2 mb-2">
                <label className="form-label text-muted">
                  Quantity <span className="text-danger">*</span>
                </label>
                <div className="d-flex  align-items-center border rounded px-2">
                  <input
                    type="number"
                    name="quantity"
                    className={`form-control border-0 shadow-none ${
                      errors.quantity ? "is-invalid" : ""
                    }`}
                    value={item.quantity}
                    onChange={(e) => changeQuantity(index, e.target.value)}
                    placeholder="Enter available quantity"
                    style={{ flex: 1 }}
                    min={1}
                    max={item.available_quantity}
                  />
                  {orderItems.request_type === "internal" && (
                    <span className="text-muted small ms-2">
                      / {item.available_quantity || 0}
                    </span>
                  )}
                </div>
                {errors[`quantity_${index}`] && (
                  <div className="invalid-feedback d-block">
                    {errors[`quantity_${index}`]}
                  </div>
                )}
              </div>

              {orderItems.request_type === "external" &&<>
               <div className="col-md-2 mb-2">
                <label className="form-label text-muted">
                  Price <span className="text-danger">*</span>
                </label>
                <div className="d-flex  align-items-center border rounded px-2">
                  <input
                    type="number"
                    name="item_total_price"
                    className={`form-control border-0 shadow-none ${
                      errors.quantity ? "is-invalid" : ""
                    }`}
                    value={item.item_total_price}
                    onChange={(e) => handlePriceChange(index, e.target.value)}
                    placeholder="Enter price"
                    style={{ flex: 1 }}
                    min={1}
                  />
                </div>
                {errors[`item_total_price_${index}`] && (
                  <div className="invalid-feedback d-block">
                    {errors[`item_total_price_${index}`]}
                  </div>
                )}
              </div>
              </>}

              <div className="col-md-2 mb-2">
                <label className="form-label text-muted">
                  Unit <span className="text-danger">*</span>
                </label>
                <select
                  name="unit"
                  className={`form-control ${errors.unit ? "is-invalid" : ""}`}
                  value={item.unit || item?.item?.unit || ""}
                  // onChange={handleChange}
                  disabled
                  // onChange={(e) =>
                  //   handleChange(index, "unit", e.target.value)
                  // }
                >
                  <option value={item.unit || item?.item?.unit}>
                    {item.unit || item?.item?.unit}
                  </option>
                </select>
                {errors.unit && (
                  <div className="invalid-feedback">{errors.unit}</div>
                )}
              </div>

              <div className="col-md-3 mb-2">
                <label className="form-label text-muted">
                  Purpose
                  {/* <span className="text-danger">*</span> */}
                </label>
                <textarea
                  name="purpose"
                  className={`form-control ${
                    errors.purpose ? "is-invalid" : ""
                  }`}
                  value={item.purpose}
                  // onChange={handleChange}
                  onChange={(e) =>
                    handleChange(index, "purpose", e.target.value)
                  }
                  placeholder="Enter item purpose"
                ></textarea>
                {errors.purpose && (
                  <div className="invalid-feedback">{errors.purpose}</div>
                )}
              </div>

              {/* <div className="col-md-3 mb-2">
                <label className="form-label text-muted">
                  Invoice
                </label>
                <input
                  type="file"
                  className="form-control"
                  name="invoice"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setFormData({ ...formData, invoice: e.target.files[0] });
                    }
                  }}
                />
              </div> */}
              <div className="col-md-2 d-flex align-items-end justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeOrderItem(index)}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* {orderItems?.length === 0 && (
          <div className="text-center py-4 text-muted">
            <i className="bi bi-cart-x fs-1 d-block mb-2"></i>
            No items added yet.
          </div>
        )} */}
      </div>
    </div>
  );
};

export default ProcurementItems;
